import { useEffect, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { IncomeReviewFieldsType, incomeReviewSchema } from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useApplicationData, useOpenBankingSteps } from '@provider';
import { APPLICATION_STEPS, OPEN_BANKING_INCOME_DETAILS } from '@utils';
import OpenBankingIncomeLoadingDetails from './OpenBankingIncomeLoadingDetails';
import { OpenBankingIncomeReview } from './OpenBankingIncomeReview';
import { useEditToIncomeVerification, useOpenBankingIncomeDetail } from './hooks';

const { TITLE } = OPEN_BANKING_INCOME_DETAILS;

function IncomeReview() {
  const { IncomeReview: incomeReview } = useApplicationData(['IncomeReview']) || {};
  const [userChoice, setUserChoice] = useState<'waiting' | 'continue' | 'cancel' | null>(null);

  const {
    completedContent,
    openBankingData,
    bankIncomeSources,
    isOpenBankingDataFetching,
    getConsentDataLazy,
    startTime,
  } = useOpenBankingIncomeDetail({ userChoice });
  const { openBankingIncomeSyncState, setIsRequestingManualIncomeEdit, setOpenBankingIncomeSyncState } =
    useOpenBankingSteps();
  const handleEditToIncomeVerification = useEditToIncomeVerification();

  const isOpenBankingSyncError = !openBankingData?.totalMonthlyIncome;
  const isOpenBankingSyncing = isOpenBankingDataFetching || openBankingIncomeSyncState.status === 'loading';

  const handleUserChoice = (choice: 'waiting' | 'continue' | 'cancel' | null) => {
    setUserChoice(choice);
  };

  const handleCancel = () => {
    // User chooses to cancel - navigate back to manual income verification
    setUserChoice('cancel');
    setOpenBankingIncomeSyncState({ status: 'idle' });
    handleEditToIncomeVerification();
  };

  const handleError = () => {
    // Timeout or error occurred - set state to idle and stay on current step
    setOpenBankingIncomeSyncState({ status: 'idle' });
  };

  return (
    <StepNavigator
      schema={incomeReviewSchema}
      name={APPLICATION_STEPS.INCOME_REVIEW}
      title={TITLE}
      defaultValues={incomeReview ?? {}}
      completedContent={completedContent}
      disableNavigation={isOpenBankingSyncing || isOpenBankingSyncError}
      onBeforeNextStep={() => {
        setIsRequestingManualIncomeEdit(false);
        return false;
      }}
    >
      {(formContext: UseFormReturn<IncomeReviewFieldsType>) => {
        useEffect(() => {
          getConsentDataLazy();
        }, []);

        if (isOpenBankingSyncing) {
          return (
            <OpenBankingIncomeLoadingDetails
              startTime={startTime}
              onCancel={handleCancel}
              onError={handleError}
              onUserChoice={handleUserChoice}
            />
          );
        }

        return (
          <FormProvider {...formContext}>
            <OpenBankingIncomeReview
              isOpenBankingSyncError={isOpenBankingSyncError}
              openBankingData={openBankingData}
              getConsentDataLazy={getConsentDataLazy}
              bankIncomeSources={bankIncomeSources}
            />
          </FormProvider>
        );
      }}
    </StepNavigator>
  );
}

export default IncomeReview;
